/**
 * GraphQL Analytics Event Parsers
 *
 * Utilities for parsing analytics events from GraphQL responses.
 */

import { AnalyticsEvent } from '@varo/graphql-schema-types';
import { Maybe } from 'shared/utils/types';

export interface GQLAnalytics {
  name: string;
  properties: Record<string, string>;
}

/**
 * Parse a GraphQL AnalyticsEvent into a simpler format
 *
 * @param event - GraphQL AnalyticsEvent object
 * @returns Parsed analytics event with name and properties
 */
export const parseGQLAnalyticsEvent = (event: AnalyticsEvent): GQLAnalytics => {
  const { name, properties: rawProperties } = event;
  const properties = rawProperties?.reduce<Record<string, string>>(
    (acc, element) => {
      acc[element.key] = element.value;
      return acc;
    },
    {},
  );
  return { name, properties };
};

/**
 * Parse an optional GraphQL AnalyticsEvent
 *
 * @param event - Optional GraphQL AnalyticsEvent object
 * @returns Parsed analytics event or null
 */
export const parseOptionalGQLAnalyticsEvent = (
  event: Maybe<AnalyticsEvent>,
): Maybe<GQLAnalytics> => {
  if (event) {
    return parseGQLAnalyticsEvent(event);
  }
  return null;
};
